import { type ReactElement, useContext, useState } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';
import { AuthContext } from '../context/AuthContext';
import { ActiveAquariumContext } from '../contexts/AquariumContext';
import { Button } from './atoms/Button/Button';
import { Heading } from './atoms/Typography/Heading';
import { Icon } from './atoms/Icon';
import { isZeovitSupplySystem } from '../utils/supplySystem';
import { SidebarNavItem } from './layout/SidebarNavItem/SidebarNavItem';
import { LegalLinks } from './legal/LegalLinks';
import { AppVersion } from './about/AppVersion';
import { OnboardingHelpButton } from './onboarding/OnboardingHelpButton';
import { FeedbackMenuButton } from './appFeedback/FeedbackMenuButton';
import { navigationConfig, type NavItem } from '../config/navigation';
import { aquariumIdFromPath, tabHref, tabMatches, visibleTabs } from '../config/navigationMatch';
import { useOptionalCapture } from './capture/CaptureContext';
import { ConfirmDialog } from './ui/ConfirmDialog/ConfirmDialog';
import './Sidebar.css';

interface SidebarProps {
  isCollapsed?: boolean;
}

function AuditLogIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="4" y="2" width="12" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 6h6M7 10h6M7 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function DashboardIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2" y="2" width="7" height="7" rx="1.5" fill="currentColor" opacity="0.8" />
      <rect x="11" y="2" width="7" height="7" rx="1.5" fill="currentColor" opacity="0.8" />
      <rect x="2" y="11" width="7" height="7" rx="1.5" fill="currentColor" opacity="0.8" />
      <rect x="11" y="11" width="7" height="7" rx="1.5" fill="currentColor" opacity="0.8" />
    </svg>
  );
}

function AquariumIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5 12 Q7.5 9 10 12 Q12.5 15 15 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function IcpIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 2v4M10 14v4M2 10h4M14 10h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function AlertsIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 2.5L17.5 15.5H2.5L10 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M10 8.5V11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="10" cy="13.5" r="0.75" fill="currentColor" />
    </svg>
  );
}

function SettingsIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.22 4.22l1.42 1.42M14.36 14.36l1.42 1.42M4.22 15.78l1.42-1.42M14.36 5.64l1.42-1.42"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function TasksIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M4 5h12M4 10h8M4 15h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M13 13l2 2 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GalleryIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="7" cy="9" r="1.5" fill="currentColor" opacity="0.8" />
      <path d="M2 14l4-4 3 3 3-3 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** A branching coral under a magnifier: the AI coral check. */
function CoralCheckIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M8 18v-7M8 11 4.5 7.5M8 11l3-4M4.5 7.5V4.5M11 7V4M8 14.5l-2.5-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="14.5" cy="13.5" r="2.75" stroke="currentColor" strokeWidth="1.5" />
      <path d="m16.5 15.5 2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** A lamp with light rays: PAR surveys and coral placement. */
function LightingIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 5.5h14l-1.5 2.5h-11L3 5.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M6 11l-1.5 3M10 11v4M14 11l1.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M3 18h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** A wallet: expenses, spend per month and year, the year-end forecast. */
function BudgetIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M15.5 6.5V4.5a1 1 0 0 0-1-1h-10A1.5 1.5 0 0 0 3 5v10a1.5 1.5 0 0 0 1.5 1.5h11a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1H4.5A1.5 1.5 0 0 1 3 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="13.5" cy="11.5" r="1" fill="currentColor" />
    </svg>
  );
}

/** Three waves: flow surveys (pumps, flow ranges, coral placement). */
function FlowIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M2.5 6q2-2 4 0t4 0 4 0 3 0M2.5 10.5q2-2 4 0t4 0 4 0 3 0M2.5 15q2-2 4 0t4 0 4 0 3 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** A speech bubble with a wave: the AI tank mentor chat. */
function MentorIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3.5 4h13a1.5 1.5 0 0 1 1.5 1.5v7a1.5 1.5 0 0 1-1.5 1.5H9l-3.5 3v-3h-2A1.5 1.5 0 0 1 2 12.5v-7A1.5 1.5 0 0 1 3.5 4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M5.5 10q1.5-2 3 0t3 0 3 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function LivestockIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M4 14c0-3 2-5 6-5s6 2 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="10" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6 14v2M14 14v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IntegrationsIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="5" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="15" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="15" cy="15" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7.5 10h3l2-5M7.5 10h3l2 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LicenseIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="7" cy="10" r="3.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10.5 10H17M14.5 10v2.5M17 10v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function AdminIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4 17c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function LogoutIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M8 3H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M13 14l3-4-3-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 10H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** An inbox with a microphone: the voice journal's "To review". */
function ReviewIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M2.5 11.5 4.5 4h11l2 7.5V16a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-4.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M2.5 11.5H7l1 2h4l1-2h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M10 6v3.5M8.5 8.5a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function OverviewIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 7v3l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function JournalIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="4" y="2" width="12" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 7h6M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function MeasurementsIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M8 2v8.5L5.5 15a3 3 0 0 0 9 0L12 10.5V2H8Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M8 2h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M6 13h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function TimelineIcon(): ReactElement {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M9 5v4l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function CommunityIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="14" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2.5 16c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12.5 12.2c.5-.4 1-.6 1.5-.6 2 0 3.5 1.6 3.5 3.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ComparisonsIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2" y="4" width="7" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="11" y="4" width="7" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 2v16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function AnalyticsIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 17h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="4" y="10" width="3" height="5" rx="0.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="8.5" y="6" width="3" height="9" rx="0.5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="13" y="3" width="3" height="12" rx="0.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function DosingIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M7 2h6v4l2 3v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V9l2-3V2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M5 9h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M10 12v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** Equipment safety (#1828): a shield with a check mark. */
function SafetyIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 2 4 4.5v4.8c0 3.9 2.6 6.8 6 8.2 3.4-1.4 6-4.3 6-8.2V4.5L10 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="m7.5 10 1.8 1.8L12.8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Equipment (light, flow, safety, devices): a wrench. */
function EquipmentIcon(): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M12.5 3.2a4 4 0 0 0-4.9 5.2L3 13a1.8 1.8 0 0 0 2.5 2.5l4.6-4.6a4 4 0 0 0 5.2-4.9l-2.4 2.4-2-.5-.5-2 2.1-2.7Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Maps a navigation item's icon key to its concrete icon element.
 * Icon keys are declared in navigation.ts as the lowercase NavIconKey union
 * (e.g. 'dashboard', 'aquarium', 'icp') and resolved here because
 * navigation.ts must stay framework-agnostic (no JSX).
 */
function resolveIcon(iconKey: NavItem['icon']): ReactElement | undefined {
  switch (iconKey) {
    case 'dashboard':
      return <DashboardIcon />;
    case 'review':
      return <ReviewIcon />;
    case 'aquarium':
      return <AquariumIcon />;
    case 'overview':
      return <OverviewIcon />;
    case 'icp':
      return <IcpIcon />;
    case 'measurements':
      return <MeasurementsIcon />;
    case 'livestock':
      return <LivestockIcon />;
    case 'journal':
      return <JournalIcon />;
    case 'gallery':
      return <GalleryIcon />;
    case 'coralCheck':
      return <CoralCheckIcon />;
    case 'lighting':
      return <LightingIcon />;
    case 'flow':
      return <FlowIcon />;
    case 'mentor':
      return <MentorIcon />;
    case 'timeline':
      return <TimelineIcon />;
    case 'community':
      return <CommunityIcon />;
    case 'comparisons':
      return <ComparisonsIcon />;
    case 'tasks':
      return <TasksIcon />;
    case 'integrations':
      return <IntegrationsIcon />;
    case 'dosing':
      return <DosingIcon />;
    case 'safety':
      return <SafetyIcon />;
    case 'equipment':
      return <EquipmentIcon />;
    case 'zeovit':
      return <Icon name="flask" size={20} />;
    case 'analytics':
      return <AnalyticsIcon />;
    case 'alerts':
      return <AlertsIcon />;
    case 'budget':
      return <BudgetIcon />;
    case 'settings':
      return <SettingsIcon />;
    case 'license':
      return <LicenseIcon />;
    case 'admin':
      return <AdminIcon />;
    case 'auditLog':
      return <AuditLogIcon />;
    case 'logout':
      return <LogoutIcon />;
    default:
      return undefined;
  }
}

/** The current path, or '' when the sidebar is rendered without a router (page tests). */
function usePathname(): string {
  const inRouter = useInRouterContext();
  // useLocation throws outside a router; the context check never changes for a mounted sidebar.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return inRouter ? useLocation().pathname : '';
}

function Sidebar({ isCollapsed = false }: SidebarProps): ReactElement {
  const { t } = useTranslation();
  // useContext (not useActiveAquarium) so Sidebar renders gracefully — with
  // aquarium-scoped links hidden — in page tests that mount it without an
  // ActiveAquariumProvider ancestor, matching the app root always providing
  // one at runtime (see App.tsx).
  const activeAquariumContext = useContext(ActiveAquariumContext);
  const activeAquariumId = activeAquariumContext?.activeAquariumId ?? null;
  const activeUsesZeovit = isZeovitSupplySystem(activeAquariumContext?.activeAquarium?.supplySystem);
  const auth = useContext(AuthContext);
  const pathname = usePathname();
  const capture = useOptionalCapture();
  const reviewCount = capture?.toReview ?? 0;
  const [confirmLogout, setConfirmLogout] = useState(false);
  const queuedNotes = capture?.queue.length ?? 0;
  // Voice notes not uploaded yet are deleted on logout (privacy D16): warn first.
  const handleLogout = (): void => {
    if (queuedNotes > 0) {
      setConfirmLogout(true);
      return;
    }
    void auth?.logout();
  };

  const idToken = auth?.state?.tokens?.idToken;
  let tokenClaims: Record<string, unknown> = {};
  if (idToken) {
    try {
      const payload = idToken.split('.')[1];
      tokenClaims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    } catch (err) {
      process.env.NODE_ENV !== 'production' &&
        // eslint-disable-next-line no-console
        console.warn('Sidebar: failed to decode idToken claims', err);
      tokenClaims = {};
    }
  }
  const groups: string[] = Array.isArray(tokenClaims['cognito:groups'])
    ? (tokenClaims['cognito:groups'] as string[])
    : [];
  const isAdmin = groups.includes('admin');

  // Filter each group's items: drop admin-only items for non-admin users and
  // aquarium-scoped items when no aquarium is active (matches the
  // pre-refactor Sidebar's `activeAquariumId != null` gate) and ZeoVit-only items
  // unless the active aquarium's supply system is ZeoVit, then drop any group
  // that becomes empty as a result.
  const tabContext = {
    aquariumId: aquariumIdFromPath(pathname) ?? activeAquariumId,
    usesZeovit: activeUsesZeovit,
  };

  const visibleGroups = navigationConfig
    .map(group => ({
      ...group,
      items: group.items.filter(item => {
        // An area shows when the user can open at least one of its tabs.
        if (item.tabs && visibleTabs(item, tabContext).length === 0) {
          return false;
        }
        if (item.requiresAdmin && !isAdmin) {
          return false;
        }
        if (item.requiresActiveAquarium && activeAquariumId == null) {
          return false;
        }
        if (item.requiresZeovitSupplySystem && !activeUsesZeovit) {
          return false;
        }
        if (item.captureReview && !capture?.available && reviewCount === 0) {
          return false;
        }
        return true;
      }),
    }))
    .filter(group => group.items.length > 0);

  return (
    // The complementary landmark is AppLayout's <aside>; this is its content.
    <div className={isCollapsed ? 'sidebar sidebar--collapsed' : 'sidebar'}>
      <nav aria-label={t('nav.mainNavigation')} className="sidebar__nav">
        <ul className="sidebar__nav-list">
          {visibleGroups.map(group => {
            const headingId = `nav-group-${group.labelKey}`;
            return (
              <li key={group.labelKey} className="sidebar-group">
                <Heading level={3} id={headingId} className="sidebar-group-label">
                  {t(group.labelKey)}
                </Heading>
                <ul aria-labelledby={headingId} className="sidebar-group__list">
                  {group.items.map(item => {
                    if (item.tabs) {
                      // An area links to its first tab the user can open and is
                      // active on every one of its tabs' pages.
                      const tabs = visibleTabs(item, tabContext);
                      return (
                        <SidebarNavItem
                          key={item.labelKey}
                          to={tabHref(tabs[0]!, tabContext.aquariumId)!}
                          icon={resolveIcon(item.icon)}
                          label={t(item.labelKey)}
                          active={item.tabs.some(tab => tabMatches(tab, pathname))}
                        />
                      );
                    }
                    return (
                      <SidebarNavItem
                        key={item.labelKey}
                        to={typeof item.to === 'function' ? item.to(activeAquariumId as string) : item.to}
                        icon={resolveIcon(item.icon)}
                        label={t(item.labelKey)}
                        end={item.end}
                        count={item.captureReview ? reviewCount : undefined}
                        countLabel={item.captureReview ? String(t('nav.reviewCount', { count: reviewCount })) : undefined}
                      />
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="sidebar__footer">
        <OnboardingHelpButton
          className="sidebar__tour-btn"
          iconClassName="sidebar__nav-icon"
          labelClassName="sidebar__nav-label"
          iconOnly={isCollapsed}
        />
        <FeedbackMenuButton
          className="sidebar__tour-btn"
          iconClassName="sidebar__nav-icon"
          labelClassName="sidebar__nav-label"
          iconOnly={isCollapsed}
        />
        <Button
          variant="ghost"
          fullWidth
          className="sidebar__logout-btn"
          onClick={handleLogout}
          aria-label={t('nav.logout')}
        >
          <span className="sidebar__nav-icon"><LogoutIcon /></span>
          {!isCollapsed && <span className="sidebar__nav-label">{t('nav.logout')}</span>}
        </Button>
        {!isCollapsed && <LegalLinks className="sidebar__legal-links" landmark={false} />}
        {!isCollapsed && <AppVersion className="sidebar__app-version" />}
      </div>
      <ConfirmDialog
        isOpen={confirmLogout}
        title={String(t('voice.queue.logoutTitle'))}
        message={String(t('voice.queue.logoutWarning', { count: queuedNotes }))}
        confirmLabel={String(t('voice.queue.logoutAnyway'))}
        cancelLabel={String(t('voice.queue.uploadNow'))}
        onConfirm={() => {
          setConfirmLogout(false);
          void auth?.logout();
        }}
        onCancel={() => {
          setConfirmLogout(false);
          capture?.retryQueued();
        }}
      />
    </div>
  );
}

export { Sidebar };
export default Sidebar;
